import { API_CONFIG } from '../api/config';

/**
 * Gateway settings API client (Spec 087).
 * Fetches and updates Equabot Gateway configuration via its REST API.
 */

export interface GatewaySettings {
    model: string;
    maxTokens: number;
    channels: {
        telegram: { enabled: boolean; botToken?: string };
        whatsapp: { enabled: boolean; phoneNumber?: string };
        slack: { enabled: boolean; webhookUrl?: string };
    };
    orchestration: {
        enabled: boolean;
        maxConcurrentAgents: number;
    };
    auth: {
        gatewayToken: string;
        tokenExpiry?: string;
    };
}

export type SettingsCategory = 'general' | 'channels' | 'models' | 'auth';

const GATEWAY_BASE = API_CONFIG.GATEWAY.PROXY_URL.replace('/agent/gateway', '');

function gatewayUrl(path: string): string {
    return `${GATEWAY_BASE}${path}`;
}

function headers(): Record<string, string> {
    const token = API_CONFIG.GATEWAY_TOKEN || localStorage.getItem('equabot_gateway_token') || '';
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

/** Fetch current Gateway configuration */
export async function fetchGatewayConfig(): Promise<GatewaySettings | null> {
    try {
        const res = await fetch(gatewayUrl('/config'), {
            headers: headers(),
            signal: AbortSignal.timeout(5000),
        });
        if (!res.ok) return null;
        return await res.json();
    } catch {
        return null;
    }
}

/** Update Gateway configuration (partial update) */
export async function updateGatewayConfig(
    patch: Partial<GatewaySettings>
): Promise<{ ok: boolean; error?: string }> {
    try {
        const res = await fetch(gatewayUrl('/config'), {
            method: 'PATCH',
            headers: headers(),
            body: JSON.stringify(patch),
            signal: AbortSignal.timeout(5000),
        });
        if (!res.ok) {
            const body = await res.text();
            return { ok: false, error: body || `HTTP ${res.status}` };
        }
        return { ok: true };
    } catch (e) {
        return { ok: false, error: e instanceof Error ? e.message : 'Network error' };
    }
}

/** Check Gateway health */
export async function checkGatewayHealth(): Promise<{
    connected: boolean;
    version?: string;
    error?: string;
}> {
    try {
        const res = await fetch(gatewayUrl('/overview'), {
            headers: headers(),
            signal: AbortSignal.timeout(3000),
        });
        if (!res.ok) return { connected: false, error: `HTTP ${res.status}` };
        const data = await res.json();
        return { connected: true, version: data.version };
    } catch (e) {
        return { connected: false, error: e instanceof Error ? e.message : 'Unreachable' };
    }
}
